import { UrlConfig } from 'config';
import { ListingModel, PaginationModel } from 'models';
import { JSONObject } from 'models/Api/types';
import { RawListingData, ListingStatus } from 'models/Listing/types';
import { ApiResponse } from 'models/Api/ApiResponse';
import { RawPaginationData } from 'models/Pagination/Pagination';
import { AjaxService } from 'services';

export class ListingApiClient {
  public static async getListingById(id: string): Promise<ListingModel> {
    const url = UrlConfig.LISTING.LISTING_BY_ID + `${id}`;
    const response: ApiResponse<RawListingData> = await AjaxService.getData(url);
    const listing = new ListingModel(response.data);
    return listing;
  }

  public static async searchListingsByQuery(query: string, page: number = 1): Promise<PaginationModel<ListingModel>> {
    let url = UrlConfig.LISTING.LISTING_SEARCH;

    const response: ApiResponse<RawPaginationData<RawListingData>> = await AjaxService.postData(url, {
      "query": query,
      "page": page,
      "processing_status": [
        ListingStatus.PENDING,
        ListingStatus.SET_PENDING,
        ListingStatus.FAKE_PENDING,
        ListingStatus.ORIGINAL_PENDING,
        ListingStatus.APPROVED,
        ListingStatus.GROUPING_PENDING
      ],
      "authentic": true
    });
    const paginatedRespose: PaginationModel<RawListingData> = new PaginationModel(response.data);
    const formattedListings = paginatedRespose.data.map((rawListing: RawListingData) => new ListingModel(rawListing));
    const formattedPaginatedResponse: PaginationModel<ListingModel> = new PaginationModel({ ...response.data, data: formattedListings, fetched: true });
    return formattedPaginatedResponse;
  }

  public static async searchListings(query: JSONObject): Promise<PaginationModel<ListingModel>> {
    let url = UrlConfig.LISTING.LISTING_SEARCH;

    if (!query['processing_status']) {
      query['processing_status'] = [
        ListingStatus.PENDING,
        ListingStatus.SET_PENDING,
        ListingStatus.FAKE_PENDING,
        ListingStatus.ORIGINAL_PENDING,
        ListingStatus.APPROVED,
        ListingStatus.GROUPING_PENDING
      ];
    }

    const response: ApiResponse<RawPaginationData<RawListingData>> = await AjaxService.postData(url, {
      "authentic": true,
      ...query
    });
    const paginatedRespose: PaginationModel<RawListingData> = new PaginationModel(response.data);
    const formattedListings = paginatedRespose.data.map((rawListing: RawListingData) => new ListingModel(rawListing));
    const formattedPaginatedResponse: PaginationModel<ListingModel> = new PaginationModel({ ...response.data, data: formattedListings, fetched: true });
    return formattedPaginatedResponse;
  }

  public static async searchListingSuggestionsForItem(query: string, artist: string, page: number = 1): Promise<PaginationModel<ListingModel>> {
    let url = UrlConfig.LISTING.LISTING_SEARCH;

    const response: ApiResponse<RawPaginationData<RawListingData>> = await AjaxService.postData(url, {
      "query": query,
      "artist": artist,
      "page": page,
      "processing_status": [
        ListingStatus.PENDING
      ],
      "authentic": true
    });
    const paginatedRespose: PaginationModel<RawListingData> = new PaginationModel(response.data);
    const formattedListings = paginatedRespose.data.map((rawListing: RawListingData) => new ListingModel(rawListing));
    const formattedPaginatedResponse: PaginationModel<ListingModel> = new PaginationModel({ ...response.data, data: formattedListings, fetched: true });
    return formattedPaginatedResponse;
  }

  public static async markListingAsFake(ids: string[]): Promise<void> {
    const url = UrlConfig.LISTING.LISTING_MARK_FAKE;
    await AjaxService.postData(url, { "listing_ids": ids });
  }

  public static async markListingAsSet(ids: string[]): Promise<void> {
    const url = UrlConfig.LISTING.LISTING_MARK_SET;
    await AjaxService.postData(url, { "listing_ids": ids });
  }

  public static async markListingAsOriginal(ids: string[]): Promise<void> {
    const url = UrlConfig.LISTING.LISTING_MARK_ORIGINAL;
    await AjaxService.postData(url, { "listing_ids": ids });
  }

  public static async rejectListing(id: string): Promise<void> {
    const url = UrlConfig.LISTING.LISTING_REJECT;
    await AjaxService.postData(url, { "listing_id": id });
  }

  public static async updateListing(id: string, data: JSONObject): Promise<ListingModel> {
    const url = UrlConfig.LISTING.LISTING_BY_ID + `${id}`;
    const response: ApiResponse<RawListingData> = await AjaxService.patchData(url, data);
    const listing = new ListingModel(response.data);
    return listing;
  }

  public static async migrateListingImage(id: string, imageUrl: string): Promise<void> {
    const url = UrlConfig.LISTING.LISTING_MIGRATE_IMAGE;
    await AjaxService.postData(url, {
      listing_id: id,
      image: imageUrl
    });
  }
}

export class DemotedListingApiClient {
  public static async getDemotedListingById(id: string): Promise<ListingModel> {
    const url = UrlConfig.DEMOTED_LISTING.LISTING_BY_ID + `${id}`;
    const response: ApiResponse<RawListingData> = await AjaxService.getData(url);
    const listing = new ListingModel(response.data);
    return listing;
  }

  public static async searchDemotedListingsByQuery(query: string, page: number = 1): Promise<PaginationModel<ListingModel>> {
    let url = UrlConfig.DEMOTED_LISTING.LISTING_SEARCH;

    const response: ApiResponse<RawPaginationData<RawListingData>> = await AjaxService.postData(url, {
      "query": query,
      "page": page,
      "authentic": true
    });
    const paginatedRespose: PaginationModel<RawListingData> = new PaginationModel(response.data);
    const formattedListings = paginatedRespose.data.map((rawListing: RawListingData) => new ListingModel(rawListing));
    const formattedPaginatedResponse: PaginationModel<ListingModel> = new PaginationModel({ ...response.data, data: formattedListings, fetched: true });
    return formattedPaginatedResponse;
  }

  public static async migrateDemotedListingImage(id: string, imageUrl: string): Promise<void> {
    const url = UrlConfig.DEMOTED_LISTING.LISTING_MIGRATE_IMAGE;
    await AjaxService.postData(url, {
      listing_id: id,
      image: imageUrl
    });
  }
}
